// ============================================================================
// ui/project-push.js — PRIME plates in the editor, then REVIEW & SEND them as a set
// ============================================================================
// The Projects Hub (`/pages/rep-projects`) merges a quote, floor plans and "renderings"
// into one branded PDF. The renderings block shipped in May as an explicit placeholder —
// `pdf/rendering-pages-pdf.js:2` says *"Short-term Photoshop-PNG-as-rendering workflow
// **until Weight Designer Bot ships**"* — so until this feature a rep who wanted a custom
// plate in a proposal mocked it up by hand and uploaded a PNG.
//
// This is the other end of that sentence. Ryan 2026-07-30, on what it has to do:
// *"the versatility to be customized based on the customers needs/wants"* — several
// weights on one page or across pages, comp bumpers in several colours, each labelled
// `45 LB` / `35 LB` / `25 LB` / `10 LB`, and if the customer only needs 45s, or only 25s
// and 10s, that is what the packet shows.
//
// ── 2026-07-31: THIS DIALOG STOPPED DOING COLOUR WORK ───────────────────────────────
// The first cut (changelog 176) put a bare `<input type="color">` on every variant row.
// Ryan used it and flagged the real problem immediately:
//
//   *"the competition bumper plate config is a little weird how you've got to design a
//    plate or plates, go into the 'Add to a project' menu, then select colors from scratch
//    again… design a plate, add that plate, close the window, change the plate color in the
//    editor, go back to the add to a project menu again, add it, repeat."*
//
// A naked colour well here is divorced from every real colour tool in the app — palette
// extraction from the logo, the eyedropper, the saved swatches, the cascade — all of which
// live in the EDITOR. So the well either produced a colour the rep couldn't judge, or it was
// ignored in favour of that close → recolour → reopen loop.
//
// His fix, and it is the right one:
//
//   *"perhaps in the projects drop-down menu there should be an 'Add to project' button which
//    primes whatever it is you just designed, then once you've primed everything you go in
//    more fully/comprehensively, see all the designs you quasi-saved/sent/favorited for a
//    project, and then send them all at once."*
//
// So the card now has TWO rows — **Prime this design** and **Review & send (N)…** — and this
// dialog became the review screen. **The colour wells, the `dyeable`/`currentColor` plumbing
// and the seeded "current design" row are DELETED.** Every staged item is already a finished
// plate: it was designed in the editor, with the editor's tools, and primed exactly as it
// stood. Nothing here may offer to re-design it.
//
// ⚠ DO NOT PUT A COLOUR (OR FONT, OR LOGO, OR LAYOUT) CONTROL BACK IN THIS DIALOG. Every one
// of them already exists upstairs, better. The swatch on a staged row is a `<span>` — a label,
// not an input — and that is the whole point of the redesign.
//
// ── WHAT THIS DIALOG OWNS, AND WHAT IT DELIBERATELY DOESN'T ─────────────────────────
// It owns WHICH plates exist: which staged designs go, and at which denominations. The
// Projects Hub owns ARRANGEMENT — which page, what grid, the order, the captions — because
// that is where a rep is already dragging the quote and the floor plan around, and a second
// arrangement UI in a second app is two places to change one thing. So the only page question
// asked here is the coarse one ("together, or one each"), and it is asked because dropping six
// loose plates onto a hub with no pages is worse than a sensible default a rep can re-drag.
//
// ── DENOMINATIONS STAY HERE, ON PURPOSE ─────────────────────────────────────────────
// The chips live on each staged row, which is where Ryan's *"if they only need 25s and 10s"*
// is two taps. That part of the flow was never what he objected to.
//   ⚠ Since WD 460 a prime SEEDS each row with the rung its configuration was designed at (the
// gate's denomination circles and the Size ladder made that a real choice — this paragraph used
// to say the weight on screen was always a category default, which stopped being true on
// 2026-08-10). A seed only ever fills an EMPTY row: chips a rep has already ticked survive a
// re-prime, exactly as they did before.
//
// ── ONE ROW PER CONFIGURATION (WD 460, 2026-09-22 — the Hartnell push) ──────────────
// A design can carry several configurations of one product — white 45s, green 25s, each an
// instance key (`…Competition Urethane#2`) owning its own plate colour. Ryan designed four,
// primed ONCE from the one he was standing on, ticked 45 / 25 / 10 on that single row and sent:
// every plate in the packet came out in that one configuration's maroon. *"In the WD, all 3 were
// different base plate colors."* They were — and a prime only ever read the live one.
//   So a prime now stages EVERY picked configuration of the live product, each with its own
// remembered colour (`bodyColorFor`) and its own rung, sharing the one envelope. The renderer
// re-opens that envelope ON the row's configuration (`openConfiguration`, state.js), so the
// buyer's per-colour ink comes with it. Other product families are untouched: their design is
// not on the stage (see the `wd-only-one-design-exists-at-a-time` note), so the rep still hops
// to them and primes there.
//
// ── A PLATE NEEDS A SAVED DESIGN, NOT JUST A DESIGN ─────────────────────────────────
// `/wd-plate-upload` derives its R2 key from `designId` and checks ownership against the
// stored `wd-design:` record, so an unsaved design has nowhere for its plates to live and gets
// a 404. Rather than refusing, the first field becomes a NAME and this saves through the same
// `/wd-design-save` the designs menu uses — one save path, one place a design can come from.
// The new id is handed back to the designs panel so the rep's next "Save" updates that design
// instead of creating a second one.
//   With a tray, ONE `designId` is the namespace for SEVERAL artworks. That is exactly what
// `envHash` is for: the R2 key gains a trailing `-{fp}` segment per staged artwork, so two
// primes with different logos at the same weight/colour/layout no longer collide. See
// design-fingerprint.js for the full argument.
//
// REP-ONLY. `projectItems()` returns [] off the portal, and an empty list means
// canvas-actions never builds the button — a buyer's cluster is exactly what it was.
// ============================================================================

import { h } from './dom.js';
import { openModal } from './modal.js';
import { isRepMode } from '../rep-mode.js';
import { matchPresetToComposition, layoutPresetsForProduct, getLayoutPreset } from '../layouts.js';
import {
  designableWeights, productSupportsBodyColor, parseProductKey, productKey, baseProductKey,
  defaultWeightFor, modelFor, BARBELL_HOUSE_SET_LB,
} from '../products.js';
import { bodyColorFor, openConfiguration } from './state.js';
import { DEFAULT_BODY_COLOR } from '../design-profile.js';
import { productLabel as specProductLabel } from '../design-spec.js';
import { renderDesignToSVGString } from '../render-engine.js';
import { fingerprintEnvelope } from '../design-fingerprint.js';
import { portalApi, isExternalPortalUser } from '../portal-api.js';
import {
  getTray, primeToTray, patchTrayItem, removeTrayItem, setTrayItemWeights, clearTray,
  trayCount, baseFor, isTrayMemoryOnly, subscribeTray,
  rememberedArrangement, rememberArrangement, rememberedProject, rememberProject,
} from './project-tray.js';

/**
 * The packet page row. A LOCAL copy of the worker's `lib/render-tiles.js` RENDER_LAYOUTS,
 * because that module lives in the rfq-worker repo and this bundle cannot import across it.
 *
 * EVERY LAYOUT IS A SINGLE ROW (Ryan 2026-07-31: *"I want them to all be level on the project,
 * and for it to allow up to 5 on a row"*) — and SIX is now offered, not just tolerated. He asked
 * for five before the grip plate's full ladder existed; the moment 2.5 and 5 lb are modelled it
 * is *"45, 35, 25, 10, 5, 2.5"* — six denominations that belong on one page, because a set split
 * across two pages stops reading as a set. Six across on a 1188pt body is ~190pt a plate, which
 * still prints legibly.
 *
 * ⚠ `lib/render-tiles.js` IS THE CANONICAL COPY. It re-normalises everything this dialog
 * sends, so the worst a drift here can do is pick a layout the server then treats as
 * `1up` — the plates still land, they just land badly arranged. Keep the two in step
 * anyway; a rep re-dragging plates because a constant went stale is a real cost.
 */
const RENDER_LAYOUTS = Object.freeze({ '1up': 1, '2up': 2, '3up': 3, '4up': 4, '5up': 5, '6up': 6 });
const MAX_TILES_PER_PAGE = Math.max(...Object.values(RENDER_LAYOUTS));

/** The tightest row that holds `n` plates. Above the ceiling the caller splits pages. */
function layoutForCount(n) {
  const entry = Object.entries(RENDER_LAYOUTS).find(([, cap]) => cap >= n);
  return entry ? entry[0] : '6up';
}

/** Matches the id shape the worker's own sanitizer mints when a page arrives without one. */
function newPageId() {
  return `rp_${Math.random().toString(36).slice(2, 10)}`;
}


/**
 * One JSON call, normalised.
 *
 * ⚠ THE PROJECT ROUTES ANSWER `{ success: true }`; the Weight Designer's own routes answer
 * `{ ok: true }`. They are different generations of the same worker and neither is going to
 * be renamed under a live Projects Hub, so this reconciles them once, here, rather than
 * leaving every call site to remember which vintage it is talking to.
 */
/* ⛔ DO NOT REINTRODUCE A LOCAL CLIENT HERE (Ryan 2026-07-31).
   This was a hand-rolled `fetch` reading `sessionStorage.accessToken` directly — which is
   character-for-character the bug that made Save answer "Unauthorized" a day earlier
   (`portal-api.js`, WD 177). Access tokens live thirty minutes and the designer is a long-dwell
   tool, so a rep who primes a set over a long session and then sends them would have hit it on
   the very first call of the batch. `portalApi` refreshes, recovers once on a 401, prefers
   whichever tab holds the fresher pair, and reconciles `{success:true}` against `{ok:true}`. */
const api = (path, opts) => portalApi(path, opts);

/**
 * The cluster icon: stacked sheets — a PACKET. Deliberately not the designs menu's folder
 * (that one means "my saved work"); this one means "the document this goes into".
 */
export const ICON_PROJECTS =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M8 3h8l4 4v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><polyline points="16 3 16 7 20 7"/>' +
  '<path d="M4 7v12a2 2 0 0 0 2 2h10"/></svg>';

/** The prime row's icon: a plate dropping into a tray. */
const ICON_PRIME =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="12" cy="8" r="4.2"/><path d="M3 15l2.4 4.2a2 2 0 0 0 1.7 1h9.8a2 2 0 0 0 1.7-1L21 15"/>' +
  '<path d="M3 15h4.2l1.2 2h7.2l1.2-2H21"/></svg>';

const ICON_TRASH =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M3 6h18M8 6V4h8v2"/><path d="M5 6l1 14h12l1-14"/></svg>';

// ---------------------------------------------------------------------------
// PRIMING — one click, and it has to feel free
// ---------------------------------------------------------------------------

/** The staged thumbnail's edge, in CSS pixels. Small on purpose: it is a RECOGNITION aid in a
 *  list of four, not a proof, and every byte of it goes into the ~5 MB localStorage budget the
 *  tray is already spending megabytes of on logo envelopes. */
const THUMB_PX = 132;

/** The SVG raster we downscale FROM. `rasterizeSVG` only accepts 1024/2048/4096 (power-of-two,
 *  for mipmaps it doesn't need here), so 1024 is the floor — and 1024 → 132 is a clean enough
 *  reduction that the ring text still reads. */
const THUMB_RASTER = 1024;

/**
 * A small 2D picture of the plate as it stands.
 *
 * ⚠ **2D, NOT `portraitPNG`.** Priming must feel free — Ryan's flow is "prime, recolour, prime,
 * recolour" four times in twenty seconds — and a WebGL portrait is a second GL context, a
 * camera fit and a 2048² read-back. That is the cost the SEND path pays once per plate, on
 * purpose, with a progress line narrating it. Here it would turn a button into a stutter.
 * `renderDesignToSVGString` + `rasterizeSVG` is the same pipeline the 2D preview uses and it
 * is milliseconds.
 *
 * Failure returns null and the row simply shows a colour chip instead. A missing thumbnail is
 * a cosmetic loss; refusing to stage the plate over one would not be.
 */
async function makeTrayThumb(profile, productKey) {
  try {
    if (!profile || typeof document === 'undefined') return null;
    const dyeable = productSupportsBodyColor(productKey);
    const svg = renderDesignToSVGString(profile, {
      weight: Number(profile.weightValue) || defaultWeightFor(productKey),
      size: 450,
      uid: 'wd-tray-thumb',
      // WHERE the slots land on this product's face — the grip plate's are the gaps between its
      // hand cutouts; everything else takes the bumper's ring. Same argument as plate-batch.
      product: modelFor(productKey),
      // A non-dyeable product renders black regardless of what the design is dyed, and the
      // thumbnail must not promise otherwise.
      bodyColorOverride: dyeable ? undefined : DEFAULT_BODY_COLOR,
    });
    /* Dynamic, and it matters: texture-pipeline.js does `import * as THREE from 'three'` at the
       top. A static import here would drag the whole Three.js chunk into the ENTRY bundle for
       every buyer who never opens this menu — the exact latency discipline main.js applies to
       the live stage. By the time a rep can prime anything the stage is up, so this resolves
       from an already-loaded chunk. */
    const { rasterizeSVG } = await import('../texture-pipeline.js');
    const raster = await rasterizeSVG(svg, { size: THUMB_RASTER });
    // Down onto a real DOM canvas: `rasterizeSVG` may hand back an OffscreenCanvas, which has
    // `convertToBlob` but no `toDataURL`, and a data URL is what localStorage can hold.
    const small = document.createElement('canvas');
    small.width = THUMB_PX;
    small.height = THUMB_PX;
    const c2d = small.getContext('2d');
    c2d.drawImage(raster, 0, 0, THUMB_PX, THUMB_PX);
    // WebP first — roughly a third of the PNG for this content — with PNG as the floor for any
    // browser that answers with a `data:image/png` anyway (toDataURL falls back silently).
    return small.toDataURL('image/webp', 0.72) || small.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Stage the plate on screen, exactly as it stands.
 *
 * The whole feature in one function: envelope + fingerprint + the three keyed dimensions, into
 * the tray, synchronously enough that the status line lands in the same beat as the click. The
 * thumbnail is deliberately NOT awaited — it is patched onto the item when it arrives, so a
 * slow raster can never make a prime feel slow.
 *
 * @returns {Promise<{ok:boolean, error?:string, count?:number, replaced?:boolean, memoryOnly?:boolean}>}
 */
export async function primeCurrentDesign(ctx) {
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) return { ok: false, error: 'There’s no design on screen to stage.' };

  // ⚠ The ENVELOPE STRING is not stable — `toAutosave` stamps `ts: Date.now()` into it — so the
  // fingerprint is taken over the parsed `design` snapshot. `fingerprintEnvelope` owns that rule
  // so no caller has to remember it. A null answer means "hash it as an un-hashed plate", which
  // is precisely the pre-2026-07-31 behaviour and still resolves.
  const fp = await fingerprintEnvelope(envelope);
  if (!fp) return { ok: false, error: 'That design couldn’t be read.' };

  const state = ctx.state;
  const liveKey = state?.ui?.product || 'bumper|Competition Urethane';
  const profile = state?.designProfile || {};
  // DERIVED, not the stored id, because emptying an arc turns a Varsity Arch into a Marquee and
  // the derived answer is the one a rep would agree with. (Same call, same reason, as
  // ui/showcase-admin.js.) One answer for every configuration: they share the layout by
  // construction — an instance owns its colour and its denominations, never its arrangement.
  const layoutPreset = matchPresetToComposition(profile, layoutPresetsForProduct(liveKey))
    || profile.layoutPreset
    || null;

  /* EVERY picked configuration of the live product, live one included — see the header. A design
     with one configuration (or no picks at all, off the gate) stages exactly the one row it always
     did. */
  const keys = configurationsOf(state, liveKey);
  let count = 0;
  let replaced = false;
  let memoryOnly = false;
  const rows = [];
  for (const key of keys) {
    const dyeable = productSupportsBodyColor(key);
    // The LIVE configuration reads its colour off the profile; a sibling reads its remembered one
    // (`savedBodyColor`), and a sibling never dyed inherits the live colour — `bodyColorFor` is the
    // one function every surface asks, so the tray agrees with the switcher's dots.
    const hex = String((dyeable && bodyColorFor(state, key)) || DEFAULT_BODY_COLOR).toUpperCase();
    // Black is the plate's UNDYED state, so it is staged as "no colour" rather than as #0b0c0f.
    // That matters beyond tidiness: the worker keys an undyed plate as `…-default-…`, so one
    // object is shared by every product that can't dye and every bumper left at stock — instead
    // of a near-duplicate per design.
    const bodyColor = (dyeable && hex !== DEFAULT_BODY_COLOR.toUpperCase()) ? hex : null;
    // The rung this configuration is designed at — the gate's circle, or the Size dial's last move
    // on it — seeds a NEW row's chips. `ctx.weightForProduct` is main.js's `weightForVisit`, the
    // same answer the stage gives on a hop; the product default is the fallback off the portal.
    const rung = Number(ctx.weightForProduct?.(key)) || defaultWeightFor(key);
    const staged = primeToTray({ fp, envelope, product: key, bodyColor, layoutPreset, seedWeights: [rung] });
    count = staged.count;
    replaced = replaced || staged.replaced;
    memoryOnly = memoryOnly || staged.memoryOnly;
    rows.push({ key, id: staged.item.id });
  }

  // Fire-and-forget, and SEQUENTIAL: one raster at a time, so four configurations do not open four
  // 1024² canvases at once on a phone. `patchTrayItem` no-ops if the rep has already removed the
  // row, which is the honest outcome for a picture nobody is waiting for.
  void (async () => {
    for (const row of rows) {
      let p = profile;
      if (row.key !== liveKey) {
        // A sibling's picture is drawn ON that sibling — its colour and its ink — through the same
        // door the renderer uses, and its composite is rebuilt for the same reason plate-batch does
        // (the envelope never carries `renderUrl`). Failure falls back to the live profile's look.
        try {
          const st = openConfiguration(envelope, row.key);
          if (st?.designProfile) {
            await ctx.rebuildLogoComposite?.(st.designProfile);
            p = st.designProfile;
          }
        } catch { p = profile; }
      }
      // eslint-disable-next-line no-await-in-loop
      const thumb = await makeTrayThumb(p, row.key);
      if (thumb) patchTrayItem(row.id, { thumb });
    }
  })();

  return { ok: true, count, replaced, memoryOnly, staged: keys.length };
}

/**
 * The picked configurations of `liveKey`'s product, in pick order, the live key always among them.
 * Reads the picks the same way `fromAutosave` does (`productKey(category, kind, instance)`), so a
 * pick from an old save with no `instance` is instance 1.
 */
function configurationsOf(state, liveKey) {
  const base = baseProductKey(liveKey);
  const keys = [];
  for (const pick of (state?.ui?.selectedProducts || [])) {
    if (!pick || !pick.category) continue;
    const key = productKey(pick.category, pick.kind, pick.instance);
    if (baseProductKey(key) === base && !keys.includes(key)) keys.push(key);
  }
  if (!keys.includes(liveKey)) keys.unshift(liveKey);
  return keys;
}

// ---------------------------------------------------------------------------
// LABELS — how a staged plate describes itself
// ---------------------------------------------------------------------------

/* The name itself lives in `design-spec.js` — the PDF sheets need the identical
   string, and this was the copy they would otherwise have been written against
   (CLAUDE.md §0b). Only the key-parsing and the empty fallback stay here. */
function productLabel(productKey) {
  const { category, kind } = parseProductKey(productKey);
  if (!category) return 'Plate';
  return specProductLabel(category, kind);
}

function layoutLabel(id) {
  if (!id) return 'As designed';
  try { return getLayoutPreset(id)?.name || 'As designed'; }
  catch { return 'As designed'; }
}

// ---------------------------------------------------------------------------
// THE REVIEW & SEND DIALOG
// ---------------------------------------------------------------------------

/**
 * Open the review-and-send dialog.
 * @param {object} ctx the app context — needs `el`, `state`, `shareEnvelope`, `status`,
 *   `suspend3D`, and (rep-only) `currentDesign` / `adoptSavedDesign` for the saved-design
 *   handshake with ui/designs-menu.js.
 */
export async function openProjectPushDialog(ctx) {
  // The design's identity, if it already has one. `null` id = never saved, which is the
  // only case that needs a name typed — see the header.
  const known = ctx.currentDesign?.() || null;
  let designId = known?.id || null;
  let designRef = known?.ref || null;
  let designName = known?.name || '';

  const status = h('p', { class: 'wd-pp-status', role: 'status' });
  const say = (m) => { status.textContent = m || ''; };

  const nameInput = h('input', {
    type: 'text', class: 'wd-dm-name', placeholder: 'e.g., Hot Springs High School',
    maxlength: '80', autocomplete: 'off', spellcheck: 'false',
    value: (ctx.state?.designProfile?.orgName || '').trim(),
  });
  const nameField = h('label', { class: 'wd-pp-field' },
    h('span', { class: 'wd-dm-listhead__label' }, 'Name this design'),
    h('span', { class: 'wd-pp-hint' }, 'Plates are stored against a saved design — this saves it first.'),
    nameInput);
  nameField.hidden = !!designId;

  /* WHICH REFERENCE NUMBER THESE PLATES WILL CARRY, said out loud (Ryan 2026-07-31): *"the saved
     file reference number in the WD determines the reference number in the project. More pushes
     does not equal more reference numbers."* Now that the ref survives a reload, the rep's second
     push genuinely reuses the first one's number — but until this line the dialog showed nothing
     either way, so "did it make a new one?" was a question only the Projects hub could answer.
     A rule the tool obeys silently is indistinguishable from one it ignores. */
  const refNote = h('p', { class: 'wd-pp-hint wd-pp-ref' }, '');
  function showRef() {
    refNote.textContent = designRef ? `Filing under ${designRef} — sending again reuses this number.` : '';
    refNote.hidden = !designRef;
  }
  showRef();

  /* Restore the arrangement this project was last sent with (Ryan 2026-07-31). Fires on every
     pick, including the rep re-picking the same one, so the dropdown always shows what this
     project actually does rather than whatever the previous project left behind. A project with
     no history leaves the current choice alone rather than resetting it — the rep may have just
     set it deliberately, and clobbering that would be a worse bug than not remembering. */
  const projectSel = h('select', {
    class: 'wd-pp-select',
    onChange: () => {
      const remembered = rememberedArrangement(projectSel.value);
      if (remembered && RENDER_ARRANGEMENTS.has(remembered)) arrangeSel.value = remembered;
    },
  },
    h('option', { value: '' }, 'Loading your projects…'));
  const rowsEl = h('div', { class: 'wd-pt-rows' });

  /** The valid 'Put them' values — so a stale remembered one can't select nothing. */
  const RENDER_ARRANGEMENTS = new Set(['one', 'product', 'each']);
  const arrangeSel = h('select', { class: 'wd-pp-select' },
    h('option', { value: 'one' }, 'All on one page'),
    // Ryan 2026-07-31: "doesn't let me put each different type of weight on its own page (all
    // bumpers on one, all grip plates on one, all dumbbells on one)". Grouped by CATEGORY, not
    // by product key — his words are the four things on the gate, so Competition Urethane and
    // Training Rubber share the bumper page rather than splitting it.
    h('option', { value: 'product' }, 'A page per product'),
    h('option', { value: 'each' }, 'One page each'));
  /* THE DEFAULT IS "A page per product" (Ryan 2026-07-31), not the first option in the list.
     A packet reads by product — all the bumpers together, all the grip plates together — and that
     is what a rep wants nearly every send, so it should not be a choice they re-make each time.
     Set here rather than by reordering the options, because the list order is the escalation the
     labels describe (everything → per product → per plate) and shuffling it to move a default
     would make the dropdown read oddly for the sake of one initial value.
       ⚠ A project that has been sent to before still wins: `rememberedArrangement` overrides this
     in `loadProjects` and on every pick. That is deliberate — this is the default for work with no
     history, and a project's own habit is more specific than a global one. */
  arrangeSel.value = 'product';

  // ── the staged list ─────────────────────────────────────────────────────────────────
  /* THIS DIALOG'S OWN WRITES MUST NOT BOUNCE BACK AT IT. `subscribeTray` exists so a prime
     landing from somewhere else (the phone's hamburger is reachable with this open) repaints the
     list — but every tap of a denomination chip also writes to the tray, and an unguarded
     subscription would rebuild every row underneath the finger that just tapped one, throwing
     away DOM focus mid-interaction. So a local write is MUTED: the store still updates, the
     listener still fires, and this dialog simply declines to repaint itself over its own edit.
     Synchronous by construction — `setTray` notifies inside the call — so the flag is never
     left standing. */
  let muted = false;
  const mute = (fn) => { muted = true; try { return fn(); } finally { muted = false; } };

  /**
   * One staged plate.
   *
   * Everything on the left of this row is a LABEL, not a control: the thumbnail, the product,
   * the colour swatch and the layout all describe a decision already made in the editor. The
   * only two things a rep can do here are choose denominations and take the plate back out.
   * See the header — do not add a third.
   */
  function stagedRow(item) {
    const chips = designableWeights(item.product); // null = free entry (the fixed barbell)
    const swatchHex = item.bodyColor || DEFAULT_BODY_COLOR;

    const chipsEl = h('div', { class: 'wd-pp-chips' });
    const freeEl = h('input', {
      type: 'text', class: 'wd-pt-free', inputmode: 'numeric',
      placeholder: 'e.g. 20, 30, 40', autocomplete: 'off', spellcheck: 'false',
      value: (item.weights || []).join(', '),
    });

    let weights = [...(item.weights || [])];
    const commit = () => mute(() => setTrayItemWeights(item.id, weights));

    function paintChips() {
      chipsEl.replaceChildren();
      for (const wt of (chips || [])) {
        const on = weights.includes(wt);
        chipsEl.appendChild(h('button', {
          type: 'button',
          class: `wd-pp-chip wd-flat-glass wd-flat-glass--flush${on ? ' is-on' : ''}`,
          'aria-pressed': on ? 'true' : 'false',
          onClick: () => {
            // At least one denomination always survives: a staged plate with no weights is a
            // row that silently contributes nothing to the send, and the rep would have to
            // work out which of four it was. Removing the PLATE is the trash button's job.
            if (on && weights.length > 1) weights = weights.filter((x) => x !== wt);
            else if (!on) weights = [...weights, wt].sort((a, b) => b - a);
            commit();
            paintChips();
            paintMode();   // the fan's hint names WHICH set it will draw, so it tracks the count
          },
        }, `${wt} lb`));
      }
    }

    freeEl.addEventListener('change', () => {
      weights = String(freeEl.value || '').split(/[,\s]+/).map(Number).filter((n) => Number.isFinite(n) && n > 0);
      if (!weights.length) weights = [defaultWeightFor(item.product)];
      freeEl.value = weights.join(', ');
      commit();
      // The fixed barbell is FREE ENTRY, so this — not the chip row — is where its count changes,
      // and it is also why the fan chip was always found greyed out: an untouched row seeds ONE
      // weight from `defaultWeightFor`, which was one short of the gate that no longer exists.
      paintMode();
    });

    if (chips) paintChips();

    const del = h('button', {
      type: 'button', class: 'wd-sc-rowdel wd-pt-del', title: 'Take this plate out', html: ICON_TRASH,
      onClick: () => { mute(() => removeTrayItem(item.id)); paintRows(); },
    });

    const thumb = item.thumb
      ? h('img', { class: 'wd-pt-thumb', src: item.thumb, alt: '', width: '54', height: '54' })
      // The fallback is a disc in the plate's own colour, which is still the single most
      // recognisable thing about a row in a four-colour sweep.
      : h('span', { class: 'wd-pt-thumb wd-pt-thumb--blank', style: { background: swatchHex }, 'aria-hidden': 'true' });

    /* ── ONE PICTURE EACH, OR THE WHOLE SET IN ONE (Ryan 2026-07-31) ──────────────────────────
       *"reps would have the option to choose which image they generate based on the customers
       needs… the rep would never select both on the same rendering — either/or."*

       So this is a two-state control on the ROW, not a third option in the arrangement dropdown:
       the arrangement decides how pages are built out of pictures, and this decides which picture
       gets made. It is also the one thing in this dialog that changes what is RENDERED rather
       than which page it lands on, which is why it sits with the denominations.

       ⚠ Only where the composition means something. `packet-fan.js` is built around a long object
       with a branded disc on its near end; a set of DISCS is already photographed better by the
       per-plate row this would replace. `plate-batch.js`'s `canFan` is the authority — this is
       the same rule stated in the UI so a rep is never offered a button that quietly does
       nothing. */
    const modeEl = h('div', { class: 'wd-pp-mode', role: 'group', 'aria-label': 'What to render' });
    const fannable = modelFor(item.product) === 'barbell';
    let mode = item.render === 'fan' ? 'fan' : 'each';
    function paintMode() {
      if (!fannable) return;
      /* ⛔ NEVER DISABLED (Ryan 2026-07-31). This used to grey out below two denominations and say
         *"Add a second weight to fan them into one image"* — which mistook the button for a way of
         COMBINING the weights a rep had picked. It isn't. Ryan: *"you design one fixed barbell, and
         then that button is always available to click, which is your way of telling the tool that
         the customer plans to buy the full 10x barbell set from 20-110 lbs."* A rep presses it
         while looking at the ONE bar they just designed, which was exactly the state that disabled
         it. `fanWeightsFor` supplies the house ten when they have not named their own, so the hint
         states which set is about to be drawn rather than asking for input that isn't needed. */
      const own = weights.length >= 2;
      modeEl.replaceChildren();
      for (const [value, label, hint] of [
        ['each', 'One image each', 'A separate picture of every bar'],
        ['fan', 'One fanned set', own
          ? `All ${weights.length} of them in a single picture`
          : `The full ${BARBELL_HOUSE_SET_LB.length}-bar set (${BARBELL_HOUSE_SET_LB[0]}–${BARBELL_HOUSE_SET_LB[BARBELL_HOUSE_SET_LB.length - 1]} lb) in a single picture — tick your own weights above to fan just those`],
      ]) {
        const on = mode === value;
        modeEl.appendChild(h('button', {
          type: 'button',
          class: `wd-pp-chip wd-flat-glass wd-flat-glass--flush${on ? ' is-on' : ''}`,
          'aria-pressed': on ? 'true' : 'false',
          title: hint,
          onClick: () => {
            mode = value;
            mute(() => patchTrayItem(item.id, { render: value }));
            paintMode();
          },
        }, label));
      }
    }
    paintMode();

    return h('div', { class: 'wd-pt-row wd-flat-glass wd-flat-glass--flush' },
      thumb,
      h('div', { class: 'wd-pt-meta' },
        h('span', { class: 'wd-pt-title' }, productLabel(item.product)),
        h('span', { class: 'wd-pt-sub' },
          h('span', { class: 'wd-pt-swatch', style: { background: swatchHex }, 'aria-hidden': 'true' }),
          `${item.bodyColor || 'Stock black'} · ${layoutLabel(item.layoutPreset)}`),
        chips ? chipsEl : freeEl,
        modeEl),
      del);
  }

  /** Re-read the tray and rebuild the list. Cheap — a tray is a handful of items. */
  function paintRows() {
    const tray = getTray();
    rowsEl.replaceChildren();
    if (!tray.items.length) {
      rowsEl.appendChild(h('p', { class: 'wd-pt-empty' },
        'Nothing staged yet — design a plate, then hit ',
        h('strong', {}, 'Prime this design'),
        '. Recolour it in the editor and prime again for each variant you want in the packet.'));
    } else {
      for (const item of tray.items) rowsEl.appendChild(stagedRow(item));
    }
    // "WEIGHTS", not "plates" (Ryan 2026-07-31) — the tray also stages dumbbells and fixed
    // barbells, neither of which is a plate. "Weights" is the word that covers the catalogue.
    countEl.textContent = tray.items.length === 1 ? '1 weight staged' : `${tray.items.length} weights staged`;
    clearBtn.hidden = !tray.items.length;
    pushBtn.disabled = !tray.items.length;
    quotaEl.hidden = !isTrayMemoryOnly();
  }

  const countEl = h('span', { class: 'wd-dm-listhead__label' }, '');
  const clearBtn = h('button', {
    type: 'button', class: 'wd-sc-mini wd-sc-mini--danger', hidden: true,
    onClick: () => { mute(() => clearTray()); paintRows(); say('Tray emptied.'); },
  }, 'Clear all');

  /* Said out loud rather than swallowed. `localStorage` is ~5 MB and ONE 4096px logo can exceed
     it on its own, so the tray falls back to in-memory for the session (project-tray.js). The
     plates are all still here and will all still send — they just won't survive a refresh, and
     a rep who reloads mid-proposal deserves to have been warned. */
  const quotaEl = h('p', { class: 'wd-pp-hint wd-pt-warn', hidden: true },
    'This browser ran out of local storage, so the staged plates are being held in memory only — '
    + 'they’ll send fine, but they won’t survive a page reload. Send them before refreshing.');

  // ── projects ────────────────────────────────────────────────────────────────────────
  let projects = [];
  async function loadProjects() {
    const r = await api('/project-list');
    if (!r.ok) { projectSel.replaceChildren(h('option', { value: '' }, 'Couldn’t load projects')); say(r.error); return; }
    projects = Array.isArray(r.projects) ? r.projects : [];
    projectSel.replaceChildren();
    if (!projects.length) {
      projectSel.appendChild(h('option', { value: '' }, 'No projects yet — create one in the Projects hub'));
      return;
    }
    projectSel.appendChild(h('option', { value: '' }, 'Choose a project…'));
    for (const p of projects) {
      const label = [p.name, p.customerName].filter(Boolean).join(' · ');
      projectSel.appendChild(h('option', { value: p.id }, label || p.id));
    }

    /* ── LAND ON THE PROJECT THIS REP IS ACTUALLY WORKING (Ryan 2026-07-31) ────────────────────
       *"it should remember their selection in the PROJECT drop-down, so the person can quickly and
       easily push to the same project, requiring a manual override if they want to push to another
       project, which is almost never the case."*

       ⚠ ONLY IF IT IS STILL IN THE LIST. A remembered id whose project has been deleted must fall
       back to "Choose a project…" rather than sit there as a selection pointing at nothing — the
       one outcome worse than picking every time is a send the rep never looked at going somewhere
       they did not choose.

       The arrangement is restored with it, because a project's remembered arrangement is a fact
       about that project (see `rememberedArrangement`) and it would be strange for picking Lakesong
       by hand to restore its layout while landing on Lakesong automatically did not. Setting
       `.value` in script fires no `change` event, so this has to be said explicitly. */
    const last = rememberedProject();
    if (last && projects.some((p) => p && p.id === last)) {
      projectSel.value = last;
      const remembered = rememberedArrangement(last);
      if (remembered && RENDER_ARRANGEMENTS.has(remembered)) arrangeSel.value = remembered;
    }
  }

  // ── saving the design, when it has never been saved ──────────────────────────────────
  /** A small deterministic portrait for the My Designs list. Failure is fine — the design
   *  saves without one, exactly as it does from the designs menu. */
  async function thumbDataUrl() {
    try {
      const blob = await ctx.snapshotPNG?.({ size: 420 });
      if (!blob) return null;
      return await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result || ''));
        fr.onerror = () => resolve(null);
        fr.readAsDataURL(blob);
      });
    } catch { return null; }
  }

  /* Does the record this design CLAIMS to be still exist?
     Only asked once the identity outlives the tab that made it (design-identity.js), and it has to
     be asked: `/wd-plate-upload` and `/wd-design-save` both 404 on a missing record
     (weight-designer-designs.js:409 / :132), so a remembered id pointing at a design the rep has
     since deleted would fail the ENTIRE push — plates, project row and all — where the old
     memory-less code would merely have made a new one. Healing beats both: the record is genuinely
     gone, so a new reference number is the honest answer, and the rep is told which one.
       One GET, on the saved path only, against an endpoint the panel already calls. */
  async function designStillExists() {
    const r = await api(`/wd-design/${encodeURIComponent(designId)}`);
    if (r.ok) return true;
    /* ONLY the worker's own word for it. `portalApi` flattens every failure to `{ok:false, error}`
       with no status, and the 404 body is literally `{error:'not-found'}` — so this is the one
       string that means the record is gone. A network blip, an expired session and a 500 all fall
       through to `true`: the push then fails and is retried, which is far better than silently
       minting a duplicate reference number every time the office wifi hiccups. */
    return r.error !== 'not-found';
  }

  async function ensureSaved(envelope) {
    if (designId && await designStillExists()) return true;
    const healing = !!designId; // the remembered design was deleted — re-file it under a new ref
    if (healing) {
      designId = null;
      designRef = null;
      ctx.adoptSavedDesign?.(null); // stop the panel (and storage) pointing at a dead record
      showRef();
    }
    const name = healing ? (designName || nameInput.value.trim()) : nameInput.value.trim();
    if (!name) { nameField.hidden = false; say('Give this design a name first.'); nameInput.focus(); return false; }
    say(healing ? 'That saved design was deleted — saving this one…' : 'Saving the design…');
    const r = await api('/wd-design-save', {
      method: 'POST',
      body: {
        name,
        design: envelope,
        thumbnailDataUrl: await thumbDataUrl(),
        meta: { product: ctx.state?.ui?.product || '', orgName: ctx.state?.designProfile?.orgName || '' },
      },
    });
    if (!r.ok) { say(r.error); return false; }
    designId = r.id;
    designRef = r.ref;
    designName = name;
    // Tell the designs menu who we are now, or the rep's next "Save" creates a SECOND
    // design with the same name and the plates stay attached to the first.
    ctx.adoptSavedDesign?.({ id: r.id, ref: r.ref, name });
    nameField.hidden = true;
    showRef(); // it has a number now — and from here on it keeps it, across reloads
    say(`Saved as ${r.ref}.`);
    return true;
  }

  // ── the push ────────────────────────────────────────────────────────────────────────
  let running = null; // AbortController while a batch is in flight

  const pushBtn = h('button', { type: 'button', class: 'wd-dm-save wd-pp-push', onClick: () => void push() }, 'Send to a project');
  const cancelBtn = h('button', {
    type: 'button', class: 'wd-sc-mini wd-pp-cancel', hidden: true,
    onClick: () => { running?.abort(); say('Stopping after this plate…'); },
  }, 'Stop');

  function busy(on) {
    pushBtn.disabled = !!on || !trayCount(getTray());
    cancelBtn.hidden = !on;
    projectSel.disabled = !!on;
    arrangeSel.disabled = !!on;
    clearBtn.disabled = !!on;
  }

  async function push() {
    const projectId = projectSel.value;
    if (!projectId) { say('Pick a project.'); projectSel.focus(); return; }

    const tray = getTray();
    /* One SEND JOB per staged plate: its own envelope (pulled from the shared base — see
       project-tray.js on why envelopes live once per fingerprint), its own fingerprint for the
       R2 key, and its own denominations. An item whose weights are still empty falls back to
       the product's sample weight rather than contributing nothing, because a row a rep can SEE
       in the list must produce a plate. */
    const items = tray.items.map((it) => ({
      envelope: baseFor(tray, it.fp),
      envHash: it.fp,
      productKey: it.product,
      bodyColor: it.bodyColor || null,
      layoutPreset: it.layoutPreset || null,
      weights: (it.weights || []).length ? it.weights : [defaultWeightFor(it.product)],
      // 'each' or 'fan' — see stagedRow. `planJobs` re-checks whether a fan is even possible for
      // this product and denomination count, so a stale value can only ever fall back to plates.
      render: it.render === 'fan' ? 'fan' : 'each',
    })).filter((it) => it.envelope && it.productKey);
    if (!items.length) { say('Nothing staged — design a plate and hit “Prime this design”.'); return; }

    busy(true);
    try {
      // The design RECORD the plates hang off is the one on screen (or the one already saved).
      // One `designId` now namespaces SEVERAL artworks, which is exactly what `envHash` makes
      // safe — see design-fingerprint.js.
      const envelope = ctx.shareEnvelope?.() || items[0].envelope;
      if (!await ensureSaved(envelope)) return;

      // Read the project BEFORE touching it. `/project-update` REPLACES `renderPages`
      // wholesale (project-routes.js:531) — it does not merge — so pushing without the
      // current array would delete every rendering page the rep already had, including
      // the Photoshop ones this feature is meant to sit beside.
      const got = await api(`/project/${encodeURIComponent(projectId)}`);
      if (!got.ok) { say(got.error); return; }
      const existingPages = Array.isArray(got.project?.renderPages) ? got.project.renderPages : [];

      running = new AbortController();
      /* The status bar's busy pill as well as the dialog's own line, and called ONCE with a
         STABLE string rather than per plate. `busy()` is delay-gated (550ms) and restarts
         that delay whenever the text changes — re-calling it per plate would mean the pill
         only ever appeared on a run where one plate took longer than half a second on its
         own, which is the opposite of "show me something is happening". The per-plate detail
         goes to the line inside the dialog, which has no delay. */
      ctx.status?.busy?.('Rendering your weights');
      /* Dynamic, and that is the point: plate-batch pulls in reveal-3d + the texture pipeline
         (Three.js). Importing it statically would drag the whole 3D chunk into the entry
         bundle for every buyer who never opens this dialog — the same latency discipline
         main.js applies to the live stage. */
      const { renderPlateBatch } = await import('../plate-batch.js');
      const { tiles, failures, aborted } = await renderPlateBatch(ctx, {
        items,
        designId,
        ref: designRef,
        signal: running.signal,
        onProgress: (done, total, label) => {
          say(label ? `Rendering ${label} — ${done + 1} of ${total}…` : `Rendered ${done} of ${total}.`);
        },
      });

      if (!tiles.length) {
        say(aborted ? 'Stopped — nothing was added.' : (failures[0]?.error || 'No plates could be rendered.'));
        return;
      }

      /* ── HEAVIEST ON THE LEFT (Ryan 2026-07-31) ──────────────────────────────────────────
         *"I want each page featuring multiple of the same kind of weight to organize them from
         largest to smallest each time. So for grip plates, 45lb on left, 2.5lb on right,
         everything else inbetween in their respective order."*

         Until now a row came out in the order the rep happened to tap the chips, so the same
         four denominations could land in a different order on two pages of one packet. A plate
         set has ONE correct reading order and it is descending.

         Sorted by PRODUCT FIRST, then weight descending — not by weight alone. `planJobs`
         already emits each product's jobs contiguously, and sorting on weight alone would
         interleave a 45 lb bumper with a 45 lb grip plate on a mixed page. Keying on the
         product's first appearance keeps each run together and merely orders within it, so
         this is right for all three arrangements rather than only the grouped one. */
      const productOrder = new Map();
      for (const t of tiles) {
        const k = String(t._productKey || '');
        if (!productOrder.has(k)) productOrder.set(k, productOrder.size);
      }
      tiles.sort((a, b) => {
        const pa = productOrder.get(String(a._productKey || '')) ?? 0;
        const pb = productOrder.get(String(b._productKey || '')) ?? 0;
        if (pa !== pb) return pa - pb;
        return (Number(b.weight) || 0) - (Number(a.weight) || 0);
      });

      /* Arrangement. "One page each" is literal; the other two both respect the row ceiling,
         so six plates become a 5-across and a 1-across rather than one page the server would
         silently truncate. Rows, not grids, since 2026-07-31 — see lib/render-tiles.js. */
      const newPages = [];
      const addRows = (group) => {
        for (let i = 0; i < group.length; i += MAX_TILES_PER_PAGE) {
          const slice = group.slice(i, i + MAX_TILES_PER_PAGE);
          newPages.push({ id: newPageId(), layout: layoutForCount(slice.length), renderings: slice });
        }
      };

      /* ── A FAN IS ALWAYS ITS OWN PAGE, WHATEVER THE ARRANGEMENT SAYS ─────────────────────────
         It is already a whole set in one picture — ten bars wide. Put it in a row beside three
         other tiles and every bar in it prints at a quarter width, which is illegible and is the
         opposite of what a rep chose it for. The arrangement dropdown decides how PLATES are
         grouped into pages; a composed set has no grouping question left to answer.
         Hero pages lead, because a set page introduces the ladder that follows it. */
      for (const t of tiles.filter((x) => x.variant === 'fan')) {
        newPages.push({ id: newPageId(), layout: '1up', renderings: [t] });
      }
      const paged = tiles.filter((t) => t.variant !== 'fan');

      if (arrangeSel.value === 'each') {
        for (const t of paged) newPages.push({ id: newPageId(), layout: '1up', renderings: [t] });
      } else if (arrangeSel.value === 'product') {
        /* A page per product CATEGORY, in the order the categories first appear — which is
           `planJobs`' product order, so the pages come out in the same sequence the batch
           rendered and a rep sees them land in the order they were told about. A category with
           more than a row's worth spills onto consecutive pages rather than mixing. */
        const groups = new Map();
        for (const t of paged) {
          const cat = String(t._productKey || '').split('|')[0] || 'other';
          if (!groups.has(cat)) groups.set(cat, []);
          groups.get(cat).push(t);
        }
        for (const group of groups.values()) addRows(group);
      } else {
        addRows(paged);
      }
      // `_productKey` was for grouping only — the worker whitelists tile fields and would drop
      // it anyway, but sending a field we know is meaningless is just noise on the wire.
      for (const pg of newPages) pg.renderings = pg.renderings.map(({ _productKey, ...t }) => t);

      say('Adding them to the project…');
      const saved = await api('/project-update', {
        method: 'POST',
        body: {
          id: projectId,
          // `addAssets` has no type whitelist (project-routes.js:553), so the DESIGN links
          // as a first-class asset with zero worker change — which is what makes it show up
          // in the hub's Linked Assets and inherit version selection later. It de-dupes on
          // type+ref, so pushing the same design twice links it once.
          addAssets: designRef ? [{ type: 'weight', ref: designRef, chainId: designId, designId }] : [],
          renderPages: [...existingPages, ...newPages],
        },
      });
      if (!saved.ok) { say(saved.error); return; }

      /* Remember how this project likes its weights arranged (Ryan 2026-07-31). Recorded on the
         SEND rather than on the dropdown's change, so it reflects what a rep actually committed
         to a packet — a value they scrolled past on the way to another one was never their
         answer, and a send that failed above should not teach the dialog anything. */
      rememberArrangement(projectId, arrangeSel.value);
      /* And WHICH project, for the same reason and at the same moment (Ryan 2026-07-31): *"once we
         push to that new project, it'll remember that one going forward."* Written only here — a
         project that was picked and then failed, or cancelled, must not become the next send's
         default, or the dialog would quietly aim at somewhere nothing has ever landed. */
      rememberProject(projectId);

      const proj = projects.find((p) => p.id === projectId);
      const many = tiles.length === 1 ? '1 weight' : `${tiles.length} weights`;
      const note = failures.length ? ` (${failures.length} couldn’t be rendered)` : '';
      say(`Added ${many} to ${proj?.name || 'the project'}${note}. Arrange them in the Projects hub.`);
      ctx.status?.success?.(`${many} added to ${proj?.name || 'the project'}.`);
      /* The tray is NOT auto-emptied. A rep who has just sent four plates to one school is one
         click from sending the same four to the next one, and silently binning work he can only
         recreate by re-designing it would be the more expensive mistake. "Clear all" is right
         there, and a partial failure leaves the failed plates staged to retry. */
    } finally {
      running = null;
      busy(false);
      ctx.status?.clearBusy?.(); // safe when the pill never appeared — which is the usual case
    }
  }

  /* No blurb (Ryan 2026-07-31 — he asked for it gone). The dialog explains itself: a list of
     staged weights, a project, and a Send. Instructions that restate visible controls cost height
     in a card that had just been fixed for being too tall, and a rep reads them once. */
  const body = h('div', { class: 'wd-pp' },
    quotaEl,
    h('div', { class: 'wd-pp-field' },
      h('div', { class: 'wd-pt-listhead' }, countEl, clearBtn),
      rowsEl),
    nameField,
    refNote,
    h('label', { class: 'wd-pp-field' },
      h('span', { class: 'wd-dm-listhead__label' }, 'Project'), projectSel),
    h('label', { class: 'wd-pp-field' },
      h('span', { class: 'wd-dm-listhead__label' }, 'Put them'), arrangeSel),
    h('div', { class: 'wd-pp-actions' }, pushBtn, cancelBtn),
    status,
  );

  /* SEED THE DENOMINATIONS, ONCE, BEFORE THE FIRST PAINT.
     Priming deliberately captures no weight (see the header), so a freshly staged row arrives with
     an empty list. `push()` would fall back to the product's sample weight anyway — but a row whose
     chips are ALL off, that nonetheless sends a 45, is a control lying about what it will do. So the
     fallback is made visible instead: the product's own default if the ladder offers it, else the
     ladder's first rung. The rep can then untick it like any other choice. */
  for (const it of getTray().items) {
    if (it.weights && it.weights.length) continue;
    const ladder = designableWeights(it.product);
    const want = defaultWeightFor(it.product);
    const seed = ladder ? (ladder.includes(want) ? want : ladder[0]) : want;
    if (Number.isFinite(seed)) mute(() => setTrayItemWeights(it.id, [seed]));
  }

  paintRows();
  // A prime landing while this dialog is open (the phone's menu can do it) repaints the list.
  const unsub = subscribeTray(() => { if (!running && !muted) paintRows(); });

  void loadProjects();
  const result = openModal({
    mount: ctx.el,
    title: 'Review & send',
    body,
    actions: [{ id: 'done', label: 'Done', variant: 'ghost' }],
  });
  // Closing the dialog stops the run. The plates already uploaded are already uploaded —
  // R2 keys are derived, so a re-run overwrites them in place rather than orphaning them —
  // but nothing further is rendered and the project is not touched.
  result.then(() => { running?.abort(); unsub(); });
  return result;
}

// ---------------------------------------------------------------------------
// THE CARD
// ---------------------------------------------------------------------------

/**
 * Card rows for the Projects cluster button. Empty off the portal → no button is built.
 *
 * TWO rows since 2026-07-31, and the second one's count is LIVE — which is the only reason
 * `createCanvasActions` grew a `setItems`. Rows are built once by `makeCard` and their labels
 * are plain strings, so the number would otherwise be frozen at whatever it was when the shell
 * booted. The shell re-calls this from the card's `onOpen` hook.
 */
export function projectItems(ctx) {
  if (!isRepMode()) return [];
  if (isExternalPortalUser()) return []; // CUSTOM_PORTALACCESS_BY_CLAUDE — Projects are team work
  const n = trayCount(getTray());
  return [
    {
      icon: ICON_PRIME,
      label: 'Prime this design',
      sub: 'Stage this plate as it stands',
      onClick: () => {
        void primeCurrentDesign(ctx).then((r) => {
          if (!r.ok) { ctx.status?.error?.(r.error); return; }
          // Several configurations of one product stage as several rows, and the line says so —
          // a rep who primed four colours and reads "Weight staged" would rightly go looking for
          // the other three.
          const what = r.staged > 1
            ? `Staged all ${r.staged} configurations of this weight`
            : (r.replaced ? 'Updated the staged weight' : 'Weight staged');
          const tail = r.count === 1 ? '1 weight ready to send.' : `${r.count} weights ready to send.`;
          ctx.status?.success?.(`${what} — ${tail}`);
        });
      },
    },
    {
      icon: ICON_PROJECTS,
      label: n ? `Review & send (${n})…` : 'Review & send…',
      sub: n === 1 ? '1 weight staged' : `${n} weights staged`,
      onClick: () => { void openProjectPushDialog(ctx); },
    },
  ];
}
